// Engagement_Dashboard_Old.tsx
import React, { useEffect, useMemo, useState } from "react";
import Highcharts from "highcharts/highstock";
import HighchartsReact from "highcharts-react-official";
import { Row, Col } from "react-bootstrap";
import Select, { SingleValue } from "react-select";
import { ThreeCircles } from "react-loader-spinner";
import { Clock, CheckCircle } from "react-bootstrap-icons";

import styles from "./EngagementDashboardOld.module.css";
import { fetchDashboardData } from "./EngagementDashboardOld.service";

import {
  ALL_OPTION,
  SelectOption,
  DashboardFilters,
  buildFiltersPayload,
  buildApprovalStatusBarOptions,
  buildSignatureStatusPieOptions,
  buildPartnerStackedBarOptions,
  buildOfficeStackedColumnOptions,
  getCurrentTaxYearValue,
  ApprovalStatusRow,
  SignatureStatusRow,
  OfficeStatusRow,
  PartnerStatusRow,
  ThemeColors,
} from "./EngagementDashboardOld.utils";
import { useGlobalStore } from "../../store/globalStore";
import { PurposeOption } from "../../client_onboarding/ClientsGrid/ClientsGrid.utils";

/**
 * Engagement_Dashboard
 * - Filters order (single select): Line of Service, Office, Partner, FY
 * - Backend does filtering; frontend just renders the pretty numbers.
 * - Filter payload values must be string[] OR omitted.
 */
const Engagement_Dashboard: React.FC = () => {
  const baseUri = process.env.REACT_APP_BASE_URI || "";

  const purposeOptionsFromStore: PurposeOption[] = useGlobalStore((s) => s.purposeOptions);
  const officeOptionsFromStore: Array<{ label: string; value: string }> = useGlobalStore((s) => s.officeLocationOptions);
  const partnerOptionsFromStore: Array<{ label: string; value: string }> = useGlobalStore((s) => s.primaryPartnerOptions);
  const myOfficeFromUserProfile: string | null = null; // user profile -> office label/value
  // ----------------------------

  const toSelectOptions = (items: any[]): SelectOption[] => {
    const mapped = items.map((x) => ({ label: x.label, value: x.value }));
    // Always include All at top
    return [ALL_OPTION, ...mapped.filter((o) => o.value !== "All")];
  };

  const lineOfServiceOptions = useMemo(
    () => toSelectOptions(purposeOptionsFromStore),
    [purposeOptionsFromStore]
  );

  const officeOptions = useMemo(
    () => toSelectOptions(officeOptionsFromStore),
    [officeOptionsFromStore]
  );

  const partnerOptions = useMemo(
    () => toSelectOptions(partnerOptionsFromStore),
    [partnerOptionsFromStore]
  );

  const fyOptions: SelectOption[] = useMemo(() => {
    // If you already have FY options from backend/store, use that.
    // Otherwise keep it simple: All + current FY only (expand later).
    const current = getCurrentTaxYearValue();
    return [ALL_OPTION, { label: current, value: current }];
  }, []);

  const findOption = (options: SelectOption[], value?: string | null): SelectOption => {
    if (!value) return ALL_OPTION;
    return options.find((o) => o.value === value) || { label: value, value };
  };

  // Default filters:
  // - Office = My office
  // - FY = current tax year
  const [selectedLOS, setSelectedLOS] = useState<SelectOption>(ALL_OPTION);
  const [selectedOffice, setSelectedOffice] = useState<SelectOption>(ALL_OPTION);
  const [selectedPartner, setSelectedPartner] = useState<SelectOption>(ALL_OPTION);
  const [selectedFY, setSelectedFY] = useState<SelectOption>(ALL_OPTION);

  useEffect(() => {
    // LOS default: All (or set first LOS if you want)
    setSelectedLOS((prev) => (prev.value !== "All" ? prev : ALL_OPTION));

    // Office default: My office (if exists), else All
    const officeDefault = myOfficeFromUserProfile
      ? findOption(officeOptions, myOfficeFromUserProfile)
      : ALL_OPTION;
    setSelectedOffice(officeDefault);

    // Partner default: All
    setSelectedPartner(ALL_OPTION);

    // FY default: current tax year
    const fyDefault = findOption(fyOptions, getCurrentTaxYearValue());
    setSelectedFY(fyDefault);
  }, [officeOptions, fyOptions, myOfficeFromUserProfile]);

  // Build backend payload using your utils contract (arrays or omit).
  const filtersForApi: DashboardFilters = useMemo(() => {
    const toArrayOrUndefined = (opt: SelectOption): string[] | undefined =>
      opt?.value && opt.value !== "All" ? [opt.value] : undefined;

    return {
      businessUnits: toArrayOrUndefined(selectedLOS),
      offices: toArrayOrUndefined(selectedOffice),
      partners: toArrayOrUndefined(selectedPartner),
      fiscalYears: toArrayOrUndefined(selectedFY), // include only if backend supports it
    };
  }, [selectedLOS, selectedOffice, selectedPartner, selectedFY]);

  const payload = useMemo(() => {
    // Your helper converts to Backend keys:
    // BusinessUnit / Office / PrimaryPartner / FiscalYear (optional)
    // If backend does NOT support FY, disable it in utils or strip it here.
    const built = buildFiltersPayload(filtersForApi);

    // If FY is not supported by backend yet, uncomment this:
    // delete built.FiscalYear;

    return built;
  }, [filtersForApi]);

  // Data
  const [loading, setLoading] = useState(true);
  const [empty, setEmpty] = useState(false);

  const [approvalRows, setApprovalRows] = useState<ApprovalStatusRow[]>([]);
  const [signatureRows, setSignatureRows] = useState<SignatureStatusRow[]>([]);
  const [officeRows, setOfficeRows] = useState<OfficeStatusRow[]>([]);
  const [partnerRows, setPartnerRows] = useState<PartnerStatusRow[]>([]);

  useEffect(() => {
    const run = async () => {
      setLoading(true);
      setEmpty(false);

      try {
        const res = await fetchDashboardData(baseUri, payload);

        const noData =
          res.approvalStatus.length === 0 &&
          res.signatureStatus.length === 0 &&
          res.office.length === 0 &&
          res.primaryPartner.length === 0;

        if (noData) {
          setEmpty(true);
          setApprovalRows([]);
          setSignatureRows([]);
          setOfficeRows([]);
          setPartnerRows([]);
          return;
        }

        setApprovalRows(
          res.approvalStatus.map((r: any) => ({
            ApprovalStatus: r.ApprovalStatus,
            TotalCount: r.TotalCount ?? r.COUNT ?? 0,
          }))
        );

        setSignatureRows(
          res.signatureStatus.map((r: any) => ({
            SignatureStatus: r.SignatureStatus,
            TotalCount: r.TotalCount ?? r.COUNT ?? 0,
          }))
        );

        setOfficeRows(
          res.office.map((r: any) => ({
            Office: r.Office,
            ApprovalStatus: r.ApprovalStatus,
            TotalCount: r.TotalCount ?? r.COUNT ?? 0,
          }))
        );

        setPartnerRows(
          res.primaryPartner.map((r: any) => ({
            PrimaryPartner: r.PrimaryPartner,
            ApprovalStatus: r.ApprovalStatus,
            TotalCount: r.TotalCount ?? r.COUNT ?? 0,
          }))
        );

      } catch (e) {
        console.error(e);
        setEmpty(true);
      } finally {
        setLoading(false);
      }
    };

    run();
  }, [baseUri, payload]);

  useEffect(() => {
    console.log("approvalRows raw", approvalRows);
  }, [approvalRows]);


  // Theme (optional) - only if you want to pass colors to partner chart scrollbar etc.
  const theme: ThemeColors = useMemo(() => {
    const root = document.documentElement;
    const css = getComputedStyle(root);
    return {
      primary: css.getPropertyValue("--theme1_primary").trim() || "#1976D2",
      tertiary: css.getPropertyValue("--theme1_tertiary").trim() || "#E3F2FD",
    };
  }, []);

  // Charts from your utils
  const approvalOptions = useMemo(
    () => buildApprovalStatusBarOptions(approvalRows),
    [approvalRows]
  );

  const signatureBundle = useMemo(
    () => buildSignatureStatusPieOptions(signatureRows),
    [signatureRows]
  );

  const partnerOptionsChart = useMemo(
    () => buildPartnerStackedBarOptions(partnerRows, theme),
    [partnerRows, theme]
  );

  const officeOptionsChart = useMemo(
    () => buildOfficeStackedColumnOptions(officeRows),
    [officeRows]
  );

  // KPIs
  const totalELs = useMemo(() => signatureBundle.total, [signatureBundle.total]);

  const signedCount = useMemo(() => {
    const row = signatureRows.find(
      (r) => String(r.SignatureStatus || "").toLowerCase().trim() === "e-signed"
    );
    return row?.TotalCount ?? 0;
  }, [signatureRows]);

  const APPROVED_STATUS = "Approved";
  const AVG_MANUAL_TIME = 25;

  const approvedCount =
    approvalRows.find((r) => r.ApprovalStatus === APPROVED_STATUS)?.TotalCount ?? 0;

  // We no longer know actualTime, so assume full manual time saved
  const totalMinutesSaved = approvedCount * AVG_MANUAL_TIME;

  return (
    <>
      {loading && (
        <div className={styles.creatorLoader}>
          <ThreeCircles
            height="50"
            width="50"
            color="#4fa94d"
            visible={true}
            ariaLabel="three-circles-rotating"
            outerCircleColor="var(--theme1_tertiary)"
            innerCircleColor="var(--theme1_secondary)"
            middleCircleColor="var(--theme1_primary)"
          />
          <div className={styles.loaderText}>Loading dashboard data</div>
        </div>
      )}

      <div className={styles.page}>
        <Row>
          <p className={styles.title}>Engagement Letter Dashboard</p>
        </Row>

        {
          // empty ? (
          //   <div className={styles.emptyState}>
          //     <p>No dashboard data available for the selected filters.</p>
          //   </div>
          // ) : 
          (
            <>
              {/* Filters: LOS, Office, Partner, FY */}
              <Row className="mb-4">
                <Col>
                  <label className={styles.filterLabel}>Line of Service</label>
                  <Select
                    value={selectedLOS}
                    onChange={(e: SingleValue<SelectOption>) => e && setSelectedLOS(e)}
                    options={lineOfServiceOptions}
                    classNamePrefix="react-select"
                  />
                </Col>

                <Col>
                  <label className={styles.filterLabel}>Office</label>
                  <Select
                    value={selectedOffice}
                    onChange={(e: SingleValue<SelectOption>) => e && setSelectedOffice(e)}
                    options={officeOptions}
                    classNamePrefix="react-select"
                  />
                </Col>

                <Col>
                  <label className={styles.filterLabel}>Partner</label>
                  <Select
                    value={selectedPartner}
                    onChange={(e: SingleValue<SelectOption>) => e && setSelectedPartner(e)}
                    options={partnerOptions}
                    classNamePrefix="react-select"
                  />
                </Col>

                <Col>
                  <label className={styles.filterLabel}>FY</label>
                  <Select
                    value={selectedFY}
                    onChange={(e: SingleValue<SelectOption>) => e && setSelectedFY(e)}
                    options={fyOptions}
                    classNamePrefix="react-select"
                  />
                </Col>
              </Row>

              {/* KPI Banner */}
              <Row className={`${styles.kpiBanner} mb-4`}>
                <Col
                  md={6}
                  className={`${styles.kpiCol} d-flex align-items-center justify-content-center mb-3 mb-md-0`}
                >
                  <Clock size={36} className="me-3" color="#1A237E" />
                  <div>
                    <h4 className={styles.kpiValue}>{totalMinutesSaved.toFixed(2)}</h4>
                    <small className={styles.kpiSub}>
                      Total Mins Saved processing {totalELs} ELs
                    </small>
                    <br />
                    <small className={styles.kpiHint}>* Avg. manual time per EL = 25 mins</small>
                  </div>
                </Col>

                <Col
                  md={6}
                  className={`${styles.kpiColRight} d-flex align-items-center justify-content-center`}
                >
                  <div className={styles.kpiDivider} />
                  <CheckCircle size={36} className="me-3" color="#1A237E" />
                  <div>
                    <h4 className={styles.kpiValue}>{signedCount}</h4>
                    <small className={styles.kpiSub}>Engagement Letters Signed</small>
                  </div>
                </Col>
              </Row>

              {/* Charts */}
              <Row className="justify-content-center text-center">
                <Col md={6} className="mb-4 d-flex justify-content-center">
                  <div className={styles.chartCard}>
                    <HighchartsReact highcharts={Highcharts} options={approvalOptions} />
                  </div>
                </Col>

                <Col md={6} className="mb-4 d-flex justify-content-center">
                  <div className={styles.chartCard}>
                    <HighchartsReact highcharts={Highcharts} options={signatureBundle.options} />
                    <div className={styles.totalFooter}>
                      <strong>Total:</strong> {totalELs}
                    </div>
                  </div>
                </Col>

                <Col md={6} className="mb-4 d-flex justify-content-center">
                  <div className={styles.chartCard}>
                    <HighchartsReact highcharts={Highcharts} options={partnerOptionsChart} />
                  </div>
                </Col>

                <Col md={6} className="mb-4 d-flex justify-content-center">
                  <div className={styles.chartCard}>
                    <HighchartsReact highcharts={Highcharts} options={officeOptionsChart} />
                  </div>
                </Col>
              </Row>
            </>
          )}
      </div>
    </>
  );
};

export default Engagement_Dashboard;
